/**
 * PartnerLanding — /partner
 * Live vendor portal landing page (not "coming soon").
 * Logo is the dominant visual element. CTAs go directly to Register/Login.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ClipboardCheck, CreditCard, BarChart3, Building2, ChevronRight, ArrowRight, Phone, Mail, MapPin } from 'lucide-react';

const FEATURES = [
  { icon: ClipboardCheck, title: 'Digital RFQs', desc: 'Receive bid requests directly in the portal and submit your best quote online.' },
  { icon: CreditCard, title: 'Payment Tracking', desc: 'Real-time visibility into invoice status, payment schedules, and outstanding amounts.' },
  { icon: BarChart3, title: 'Performance Dashboard', desc: 'Track your on-time delivery, quality scores, and overall vendor rating.' },
  { icon: Building2, title: 'Empanelment', desc: 'Complete your pre-qualification questionnaire and get approved to receive RFQs.' },
];

const STATS = [
  { num: '550+', label: 'Projects\nCompleted' },
  { num: '5,000+', label: 'Vendor\nPartners' },
  { num: '38+', label: 'Years of\nLegacy' },
  { num: '₹500Cr+', label: 'Annual\nProcurement' },
];

export default function PartnerLanding() {
  return (
    <div className="min-h-screen bg-[#0F172A] text-white font-sans flex flex-col" data-testid="partner-landing">
      {/* Header */}
      <header className="border-b border-white/10 bg-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white/60 hover:text-white text-sm font-medium transition-colors">
            ← Back to Ellora EPC
          </Link>
          <Link to="/partner/login" className="text-white/70 hover:text-white text-sm font-medium transition-colors">
            Already a partner? <span className="text-white font-semibold">Sign in →</span>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-16 text-center">
        {/* LOGO — dominant element */}
        <div className="mb-6">
          <img
            src="/ellora-logo-blue.png"
            alt="Ellora EPC"
            className="h-20 sm:h-24 w-auto mx-auto"
            style={{ filter: 'brightness(0) invert(1)' }}
          />
        </div>
        <div className="flex items-center gap-3 mb-8">
          <div className="h-px bg-white/20 w-16" />
          <span className="text-white/50 text-[11px] font-bold uppercase tracking-widest">Partner Portal</span>
          <div className="h-px bg-white/20 w-16" />
        </div>

        {/* Headline — smaller than logo */}
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight max-w-xl mb-4">
          Your Gateway to Ellora EPC Projects
        </h1>
        <p className="text-white/65 max-w-lg text-base leading-relaxed mb-10">
          Join 5,000+ vendor partners supplying materials and services across 550+ completed projects.
        </p>

        {/* Primary CTAs */}
        <div className="flex flex-col sm:flex-row gap-3 mb-12">
          <Link to="/partner/register"
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-[#3b4872] hover:bg-[#2d3856] text-white font-bold rounded-sm transition-colors text-sm"
            data-testid="partner-register-cta">
            Register as Partner <ChevronRight size={15} />
          </Link>
          <Link to="/partner/login"
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border border-white/30 text-white hover:bg-white/10 rounded-sm transition-colors text-sm font-semibold"
            data-testid="partner-login-cta">
            Partner Login
          </Link>
        </div>
        <p className="text-white/40 text-xs mb-16">
          Already submitted your application?{' '}
          <Link to="/partner/login" className="text-white/70 hover:text-white underline">
            Sign in to track status →
          </Link>
        </p>

        {/* Stats bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl w-full mb-16">
          {STATS.map((s) => (
            <div key={s.num} className="bg-white/8 border border-white/10 rounded-sm p-4 text-center">
              <div className="text-2xl font-extrabold text-white">{s.num}</div>
              <div className="text-[11px] text-white/50 mt-1 whitespace-pre-line leading-tight">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Features */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl w-full">
          {FEATURES.map((f) => (
            <div key={f.title} className="bg-white/8 border border-white/10 rounded-sm p-5 text-left hover:bg-white/12 transition-colors">
              <f.icon size={20} className="text-white/60 mb-3" />
              <div className="font-bold text-sm mb-1.5">{f.title}</div>
              <div className="text-white/55 text-xs leading-relaxed">{f.desc}</div>
            </div>
          ))}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-6 text-center">
        <p className="text-white/35 text-xs">
          © {new Date().getFullYear()} Ellora EPC Pvt. Ltd. ·{' '}
          <Link to="/privacy" className="hover:text-white/60">Privacy Policy</Link>
        </p>
      </footer>
    </div>
  );
}
